/**
 * EventTypeCard Component
 *
 * Displays an event type with its template roles.
 * Supports editing, deleting, and managing roles.
 */

import { useState } from 'react';
import {
  EventType,
  deleteEventType,
  createEventTypeRole,
  updateEventTypeRole,
  deleteEventTypeRole,
} from '../../api/eventTypes';
import { getErrorMessage } from '../../api/client';

interface EventTypeCardProps {
  eventType: EventType;
  onEdit: (eventType: EventType) => void;
  onDuplicate: (eventType: EventType) => void;
  onDeleted: () => void;
  onUpdated: () => void;
}

export default function EventTypeCard({
  eventType,
  onEdit,
  onDuplicate,
  onDeleted,
  onUpdated,
}: EventTypeCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddRole, setShowAddRole] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // New role form state
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleCount, setNewRoleCount] = useState(1);
  const [newRoleDesc, setNewRoleDesc] = useState('');
  const [isAddingRole, setIsAddingRole] = useState(false);

  // Edit role form state
  const [editRoleName, setEditRoleName] = useState('');
  const [editRoleCount, setEditRoleCount] = useState(1);
  const [editRoleDesc, setEditRoleDesc] = useState('');
  const [isSavingRole, setIsSavingRole] = useState(false);

  const totalCrewNeeded = eventType.templateRoles.reduce(
    (sum, role) => sum + role.requiredCount,
    0
  );

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      await deleteEventType(eventType.id);
      onDeleted();
    } catch (err) {
      setError(getErrorMessage(err));
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRoleName.trim()) return;

    setIsAddingRole(true);
    setError(null);
    try {
      await createEventTypeRole(eventType.id, {
        roleName: newRoleName.trim(),
        requiredCount: newRoleCount,
        description: newRoleDesc.trim() || null,
      });
      setNewRoleName('');
      setNewRoleCount(1);
      setNewRoleDesc('');
      setShowAddRole(false);
      onUpdated();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsAddingRole(false);
    }
  };

  const handleStartEditRole = (role: EventType['templateRoles'][0]) => {
    setEditingRoleId(role.id);
    setEditRoleName(role.roleName);
    setEditRoleCount(role.requiredCount);
    setEditRoleDesc(role.description || '');
  };

  const handleSaveRole = async (roleId: string) => {
    if (!editRoleName.trim()) return;

    setIsSavingRole(true);
    setError(null);
    try {
      await updateEventTypeRole(eventType.id, roleId, {
        roleName: editRoleName.trim(),
        requiredCount: editRoleCount,
        description: editRoleDesc.trim() || null,
      });
      setEditingRoleId(null);
      onUpdated();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSavingRole(false);
    }
  };

  const handleDeleteRole = async (roleId: string) => {
    setError(null);
    try {
      await deleteEventTypeRole(eventType.id, roleId);
      onUpdated();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <ChevronIcon className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
          </button>
          <div className="min-w-0 flex-1">
            <h3 className="font-medium text-gray-900 truncate">{eventType.name}</h3>
            <p className="text-sm text-gray-500">
              {eventType.templateRoles.length} role{eventType.templateRoles.length !== 1 ? 's' : ''} · {totalCrewNeeded} crew needed
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onEdit(eventType)}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            title="Edit event type"
          >
            <EditIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDuplicate(eventType)}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            title="Duplicate event type"
          >
            <CopyIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
            title="Delete event type"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-4 mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div className="mx-4 mb-3 p-3 bg-red-50 border border-red-200 rounded">
          <p className="text-sm text-red-700 mb-2">
            Delete "{eventType.name}"? This cannot be undone.
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
              className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Expanded content - Roles */}
      {isExpanded && (
        <div className="border-t border-gray-200">
          {/* Description */}
          {eventType.description && (
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <p className="text-sm text-gray-600">{eventType.description}</p>
            </div>
          )}

          {/* Roles list */}
          <div className="px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Template Roles</span>
              <button
                onClick={() => setShowAddRole(true)}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                + Add Role
              </button>
            </div>

            {eventType.templateRoles.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No roles defined</p>
            ) : (
              <div className="space-y-2">
                {eventType.templateRoles.map((role) => (
                  <div
                    key={role.id}
                    className="flex items-center gap-2 p-2 bg-gray-50 rounded"
                  >
                    {editingRoleId === role.id ? (
                      // Edit mode
                      <div className="flex-1 flex items-center gap-2">
                        <input
                          type="text"
                          value={editRoleName}
                          onChange={(e) => setEditRoleName(e.target.value)}
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                          placeholder="Role name"
                        />
                        <input
                          type="number"
                          min={1}
                          max={50}
                          value={editRoleCount}
                          onChange={(e) => setEditRoleCount(parseInt(e.target.value) || 1)}
                          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded"
                        />
                        <button
                          onClick={() => handleSaveRole(role.id)}
                          disabled={isSavingRole || !editRoleName.trim()}
                          className="px-2 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingRoleId(null)}
                          className="px-2 py-1 text-sm text-gray-600 hover:bg-gray-200 rounded"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      // View mode
                      <>
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-900">
                            {role.roleName}
                          </span>
                          <span className="text-sm text-gray-500 ml-2">
                            × {role.requiredCount}
                          </span>
                          {role.description && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              {role.description}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => handleStartEditRole(role)}
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="Edit role"
                        >
                          <EditIcon className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteRole(role.id)}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="Delete role"
                        >
                          <TrashIcon className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Add role form */}
            {showAddRole && (
              <form onSubmit={handleAddRole} className="mt-3 p-3 bg-surface-panel border border-hairline border-l-[3px] border-l-rack">
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Role Name
                    </label>
                    <input
                      type="text"
                      value={newRoleName}
                      onChange={(e) => setNewRoleName(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                      placeholder="e.g., Camera Operator"
                    />
                  </div>
                  <div className="w-20">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Count
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={50}
                      value={newRoleCount}
                      onChange={(e) => setNewRoleCount(parseInt(e.target.value) || 1)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isAddingRole || !newRoleName.trim()}
                    className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
                  >
                    {isAddingRole ? 'Adding...' : 'Add'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddRole(false);
                      setNewRoleName('');
                      setNewRoleCount(1);
                      setNewRoleDesc('');
                    }}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Icons
// =============================================================================

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function EditIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
      />
    </svg>
  );
}

function CopyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}
